import { createApi, retry } from '@reduxjs/toolkit/query/react';
import createAxiosInstance from './axiosInstance';
import { urlTemplates } from './urls';
import config from 'utils/config';
import { API_BACKDOOR_KEY } from 'utils/constants';
import { getCookie } from 'utils/commonFunctions';

// Create separate Axios instances for api and feeds
const apiAxiosInstance = createAxiosInstance(process.env.API_BASE_URL);
const feedsAxiosInstance = createAxiosInstance(process.env.FEEDS_BASE_URL);

const axiosBaseQuery =
  (axiosInstance) =>
  async ({ url, method, data, params, headers, ...rest }) => {
    try {
      const result = await axiosInstance({
        url,
        method,
        data,
        params,
        headers,
        ...rest
      });

      return { data: result.data };
    } catch (error) {
      return {
        error: {
          ...error?.data
        }
      };
    }
  };

const baseQueryWithRetry = (axiosInstance) =>
  retry(axiosBaseQuery(axiosInstance), { maxRetries: 0 });

// Create separate base queries with retry for api and feeds
const apiBaseQueryWithRetry = baseQueryWithRetry(apiAxiosInstance);
const feedsBaseQueryWithRetry = baseQueryWithRetry(feedsAxiosInstance);

// Create separate API instances for api and feeds
export const api = createApi({
  reducerPath: 'apis',
  baseQuery: apiBaseQueryWithRetry,
  endpoints: (builder) => ({
    // Endpoint for fan login
    fanLogin: builder.mutation({
      query: (platformId) => ({
        url: urlTemplates?.fanLogin,
        method: 'POST',
        data: { platformId: config.platformId }
        //  params: { enti }
      })
    }),
    // Endpoint for media login
    mediaLogin: builder.mutation({
      query: ({ platformId, businessName, mediaCode, emailId }) => ({
        url: urlTemplates?.mediaLogin,
        method: 'POST',
        data: { platformId, businessName, mediaCode, emailId }
        // params: { backdoor: 'horcrux' }
      })
    }),
    // Endpoint for federation login
    federationLogin: builder.mutation({
      query: ({ platformId, countryToken }) => ({
        url: urlTemplates?.federationLogin,
        method: 'POST',
        data: { platformId, countryToken }
        // params: { backdoor: 'horcrux' }
      })
    }),
    //submit vote
    submitVote: builder.mutation({
      query: ({ playerId, playerCategory }) => ({
        url: urlTemplates?.submitVote,
        method: 'POST',
        data: {
          playerId,
          playerCategory
        }
        //  params: { backdoor: 'horcrux' }
      })
    }),
    createTeam: builder.mutation({
      query: (payload) => ({
        url: urlTemplates?.createTeam,
        method: 'POST',
        data: payload,
        // params: { backdoor: API_BACKDOOR_KEY },
        addBuster: true
      })
    }),

    postWebLogin: builder.mutation({
      query: ({ postData }) => ({
        url: urlTemplates?.postLogin,
        method: 'POST',
        // headers: {
        //   // "x-icc-token": postData,
        //   "x-access-token": postData,
        // },
        headers: postData,
        data: {
          optType: 1,
          platformId: 0,
          userId: 0,
          clientId: 1,
          gameName: 'top-picks',
          teamId: 0,
          favTeamId: 0
        }
        // params: { backdoor: API_BACKDOOR_KEY }
      })
    }),
    webviewLogin: builder.mutation({
      query: ({ token }) => ({
        url: urlTemplates.webviewLogin,
        method: 'POST',
        headers: {
          'x-icc-token': token
        },
        data: {
          optType: 1,
          platformId: 3,
          userId: 0,
          clientId: 1,
          gameName: 'top-picks'
        }
      })
    }),
    getGamerCard: builder.query({
      query: ({ guid }) => ({
        url: urlTemplates?.getGamerCard.replace('{{guid}}', guid),
        method: 'GET',
        //  params: { backdoor: API_BACKDOOR_KEY },
        addBuster: true
      }),
      keepUnusedDataFor: 100000000000
    }),
    getUserPhaseWisePlayers: builder.query({
      query: ({ guid }) => ({
        url: urlTemplates?.getUserPhaseWisePlayers.replace('{{guid}}', guid),
        method: 'GET',
        // params: { backdoor: API_BACKDOOR_KEY },
        addBuster: true
      }),
      keepUnusedDataFor: 100000000000
    }),
    getUserCreatedTeam: builder.query({
      query: ({ guid, gamedayId }) => ({
        url: urlTemplates?.getUserCreatedTeam.replace('{{guid}}', guid),
        method: 'GET',
        params: { gamedayId: gamedayId, teamNo: 1 },
        addBuster: true
      }),
      refectchOnMountOrArgChange: true,
      keepUnusedDataFor: 100000000000
    }),
    getTeamCreatedGamedays: builder.query({
      query: ({ guid }) => ({
        url: urlTemplates?.getTeamCreatedGamedays.replace('{{guid}}', guid),
        method: 'GET',
        addBuster: true
      }),
      refectchOnMountOrArgChange: true,
      keepUnusedDataFor: 100000000000
    }),
    getTeamNameValidate: builder.mutation({
      query: ({ guid, teamName }) => ({
        url: urlTemplates?.getTeamNameValidate.replace('{{guid}}', guid),
        method: 'POST',
        params: {
          TeamName: teamName
        }
      })
    }),
    getGlobalRank: builder.query({
      query: ({ guid, gamedayId, optType }) => ({
        url: urlTemplates?.getGlobalRank.replace('{{guid}}', guid),
        method: 'GET',
        params: { gamedayId: gamedayId, optType: optType },
        addBuster: true
      }),
      refectchOnMountOrArgChange: true,
      keepUnusedDataFor: 100000000000
    }),

    getOverviewTeam: builder.query({
      query: ({ guid, gamedayId, optType, phaseId, arrGamedayId }) => ({
        url: urlTemplates?.getOverviewTeam.replace('{{guid}}', guid),
        method: 'GET',
        params: {
          gamedayId: gamedayId,
          optType: optType,
          phaseId: phaseId,
          arrGamedayId: arrGamedayId
        },
        addBuster: true
      }),

      refectchOnMountOrArgChange: true,
      keepUnusedDataFor: 100000000000
    }),
    getOtherUserTeam: builder.query({
      query: ({ guid, gamedayId, teamId, optType, socialId }) => ({
        url: urlTemplates?.otherUserTeam.replace('{{guid}}', guid),
        method: 'GET',
        params: {
          gamedayId: gamedayId,
          teamId: teamId,
          optType: optType,
          socialId: socialId
        },
        refectchOnMountOrArgChange: true,
        keepUnusedDataFor: 100000000000
      })
    })
  }),

  // keepUnusedDataFor: 10000000000000,
  refetchOnReconnect: true
});

export const feeds = createApi({
  reducerPath: 'feeds',
  baseQuery: feedsBaseQueryWithRetry,
  endpoints: (builder) => ({
    getPlayerCardsFixture: builder.query({
      query: ({ playerId }) => ({
        url: urlTemplates?.playerCardsFixture.replace('{PlayerId}', playerId),
        method: 'GET'
        // params: { gamedayId: gamedayId, optType: optType },
      }),
      refectchOnMountOrArgChange: true,
      keepUnusedDataFor: 100000000000
    }),
    getPlayerCardsStats: builder.query({
      query: ({ playerId, gamedayId }) => ({
        url: urlTemplates?.playerCardsStats
          .replace('{PlayerId}', playerId)
          ?.replace('{gamedayId}', gamedayId),
        method: 'GET'
        // params: { gamedayId: gamedayId, optType: optType },
      }),
      refectchOnMountOrArgChange: true,
      keepUnusedDataFor: 100000000000
    })
  }),
  keepUnusedDataFor: 1000000000000,
  refetchOnReconnect: true
});

export const {
  useFanLoginMutation,
  useMediaLoginMutation,
  useFederationLoginMutation,
  useSubmitVoteMutation,
  usePostWebLoginMutation,
  useWebviewLoginMutation,
  useCreateTeamMutation,
  useGetGamerCardQuery,
  useGetUserCreatedTeamQuery,
  useGetUserPhaseWisePlayersQuery,
  useGetTeamCreatedGamedaysQuery,
  useGetGlobalRankQuery,
  useGetTeamNameValidateMutation,
  useGetOverviewTeamQuery,
  useGetOtherUserTeamQuery
} = api;
export const { useGetPlayerCardsFixtureQuery, useGetPlayerCardsStatsQuery } = feeds;

export const Api = api;
export const Feeds = feeds;
